import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { setSideNavForcedActiveLink } from "../../../../../../../../middleware/actions/sideNavAction";
import { useFormik } from "formik";
import * as Yup from "yup";
import { imageValidationSchema } from "../../../../../../../helper/validationSchema/productValidationSchema";
import { actionSuccess, actionError, showLoader, hideLoader } from "../../../../../../../../middleware/actions/utilityAction";
import {
  addProductImages,
  editProductImage,
  fetchProductImageUrl,
} from "../../../../../../../../middleware/services/productApi";
import { PRODUCT_LOGO_IMG_SIZE } from "../../../../../../../../config";
import { useHistory } from "react-router-dom";
import { setBreadCrumb } from "middleware/actions/breadCrumbAction";
import { fetchSingleCompany } from "middleware/services/companyApi";
import { accessRightActionCheck } from "utilities";
import { COMMON_ACCESS_ACTION } from "../../../../../../../../constants";

const ImageModal = (props) => {
  const { accessRights: aR } = props;
  const dispatch = useDispatch();
  const [imageError, setImageError] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [productName, setProductName] = useState("");
  const [interfaceActionAccess, setInterfaceActionAccess] = useState([]);
  const { companyId, productId, imageId } = props.computedMatch?.params || {};
  const { editData } = props;
  const productImageRef = useRef(null);
  const productImageInputRef = useRef(null);
  const split_url = props.location.pathname.split("/");
  const backPath = editData ? `${split_url.slice(0, -3).join("/")}/edit#overview` : `${split_url.slice(0, -2).join("/")}/edit#overview`;
  const history = useHistory();
  const { productDetails = {} } = useSelector((state) => state.productState);

  const breadCrumbLinks = useMemo(() => {
    const links = [{ linkUrl: "/company", linkName: `Company`, linkActive: false }];
    if (companyId && companyName) {
      links.push({ linkUrl: `/company/${companyId}/edit#basic`, linkName: companyName, linkActive: false });
      links.push({ linkUrl: `/company/${companyId}/edit#basic`, linkName: aR.moduleName, linkActive: false });
    }

    if (productId && productName) {
      links.push({
        linkUrl: `/company/${companyId}/product/${productId}/edit#overview`,
        linkName: productName,
        linkActive: false,
      });
      links.push({
        linkUrl: `/company/${companyId}/product/${productId}/edit#overview`,
        linkName: "Image",
        linkActive: false,
      });
      links.push({ linkUrl: ``, linkName: props.editData ? "Edit" : "Add New", linkActive: true });
    }

    return links;
  }, [companyId, companyName, productId, productName]);

  /* Interface action access from common access */
  useEffect(() => {
    let interfaceName = "Overview";
    let actionAccess = accessRightActionCheck(aR.moduleName, interfaceName);
    setInterfaceActionAccess(actionAccess);
  }, []);

  /* Common access */
  const accessPermissionAdd = interfaceActionAccess?.includes(COMMON_ACCESS_ACTION.ADD_IMAGES);

  const getCompanyName = async () => {
    if (companyId) {
      if (Object.keys(productDetails).length > 0) {
        setCompanyName(productDetails.productCompany.companyName);
      } else {
        const fields = { fields: ["id", "companyName"] };
        try {
          const response = await fetchSingleCompany(companyId, { ...fields });
          setCompanyName(response.data.data.companyName);
        } catch (error) {
          dispatch(actionError(error.data.message || "something went wrong."));
        }
      }
    }
  };

  useEffect(() => {
    setProductName(productDetails.productName);
    getCompanyName();
  }, [productDetails, dispatch]);

  useEffect(() => {
    dispatch(setBreadCrumb(breadCrumbLinks));

    return () => {
      dispatch(setBreadCrumb([]));
    };
  }, [dispatch, breadCrumbLinks]);

  const onSaveData = async () => {
    if (!formik.values || !formik.values.imageFile) {
      console.log("No image file selected");
      return;
    }
    if (formik.values.imageFile.size > PRODUCT_LOGO_IMG_SIZE) {
      dispatch(actionError("File size is more than 10 MB."));
      return;
    }
    const formData = new FormData();
    formData.append("file", formik.values.imageFile);
    dispatch(showLoader());
    try {
      await addProductImages(productId, formData);
      dispatch(hideLoader());
      dispatch(actionSuccess("Image saved successfully."));
      handleReset();
      history.push(backPath, { shouldRefresh: true });
    } catch (error) {
      productImageRef.current.value = "";
      dispatch(actionError(error.data.message || "something went wrong."));
    } finally {
      dispatch(hideLoader());
    }
  };

  const readProductImage = (imageFile) => {
    let reader = new FileReader();
    reader.onload = (e) => {
      productImageRef.current.setAttribute("src", e.target.result);
    };

    reader.readAsDataURL(imageFile);
  };

  const onUpdateData = async () => {
    if (!formik.values || !formik.values?.imageFile) {
      console.log("No image file selected");
      return;
    }
    const formData = new FormData();
    formData.append("file", formik.values.imageFile);
    try {
      dispatch(showLoader());
      await editProductImage(productId, imageId, formData);
      dispatch(hideLoader());
      dispatch(actionSuccess("Image updated successfully"));
      handleReset();
      history.push(backPath, { shouldRefresh: true });
    } catch (error) {
      dispatch(actionError(error.data.message || "Something went wrong."));
    } finally {
      dispatch(hideLoader());
    }
  };

  const initialFormValues = {
    imageFile: "",
  };

  const formik = useFormik({
    initialValues: {
      ...initialFormValues,
    },
    validationSchema: Yup.object().shape(imageValidationSchema),
  });

  const setImg = () => {
    if (productDetails && Array.isArray(productDetails?.productImages)) {
      // Find the image object with the matching id
      const imgObj = productDetails.productImages.find((img) => img.id === imageId);

      if (imgObj) {
        const imgUrl = fetchProductImageUrl(imgObj.imgFileName, imgObj.imgName);
        if (productImageRef.current) {
          productImageRef.current.setAttribute("src", imgUrl + "?t=" + Date.now());
        }
      } else if (productImageRef.current) {
        productImageRef.current.setAttribute("src", "/images/default_company_image.jpg");
      }
    } else if (productImageRef.current) {
      productImageRef.current.setAttribute("src", "/images/default_company_image.jpg");
    }
  };

  useEffect(() => {
    dispatch(setSideNavForcedActiveLink("/product"));
    if (editData && productDetails) {
      setImg();
    }
    return () => {
      dispatch(setSideNavForcedActiveLink());
    };
  }, [editData, productDetails, productId, imageId]);

  const handleReset = () => {
    formik.resetForm();
    productImageInputRef.current.value = "";
    setImageError("");
    if (editData) {
      formik.setValues();
    }
  };
  const saveImageData = async () => {
    const { values } = formik;
    if (!values?.imageFile || values?.imageFile?.length === 0) {
      console.log("No image file selected");
      return;
    }
    if (values.imageFile.size > PRODUCT_LOGO_IMG_SIZE) {
      formik.setFieldError("imageFile", "File size is more than 10 MB");
      return;
    }
    onSaveData();
  };
  const handleSubmitDocument = () => {
    if (editData && !productImageInputRef.current?.files?.length) {
      history.push(backPath);
      return;
    }
    formik.validateForm().then((res) => {
      if (Object.keys(res).length) {
        const touched = {};
        Object.keys(res).forEach((field) => {
          touched[field] = true;
        });
        formik.setFormikState({ ...formik, touched: touched, errors: res });
        const firstError = res[Object.keys(res)[0]];
        setImageError(typeof firstError === "string" ? firstError : JSON.stringify(firstError));
      } else if (editData) {
        onUpdateData();
        handleReset();
      } else {
        saveImageData();
      }
    });
  };

  return (
    <div className="p-0 mx-3" style={{ backgroundColor: "#fff" }}>
      <div className="p-2 border custom-bg-grey text-white rounded-top">Products &amp; Technology - Images</div>
      <div className="d-flex flex-column justify-content-between border border-top-0" style={{ height: "82vh" }}>
      <div className="p-4">
  <p style={{ fontSize: "14px", fontWeight: "700", color: "black", marginBottom: ".75rem" }}>Upload Image *</p>
  <div className="row m-0">
    <div className="product-logo-holder p-0">
      <div>
        <img
          src={
            Object.keys(formik.values?.imageFile || {}).length > 0
              ? fetchProductImageUrl(formik.values.imageFile?.name)
              : "/images/default_company_image.jpg"
          }
          alt="Product-image"
          ref={productImageRef}
          className="object-cover"
        />
      </div>
      <div className="product-logo-action" style={{ top: "2px", right: "6px" }}>
        <span onClick={() => productImageInputRef.current?.click()}>
          <i className="fa fa-pencil-alt" aria-hidden="true"></i>
                </span>
        {formik.values?.imageFile && (        
        <span onClick={() => {
          formik.setFieldValue("imageFile", null);
          if (productImageInputRef.current) {
            productImageInputRef.current.value = "";
          }
          setImageError("");
          if (productImageRef.current) {
            productImageRef.current.src = "/images/default_company_image.jpg";
          }
        }}>
          <i className="fa fa-trash ml-1" aria-hidden="true"></i>
                  </span>
        )}
                  
      </div>
      <input
        type="file"
        className="d-none"
        accept=".jpg, .jpeg, .png"
        id="productImage"
        name="productImage"
        ref={productImageInputRef}
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) {
            const file = files[0];
            if (["image/png", "image/jpg", "image/jpeg"].includes(file.type) && file.size < PRODUCT_LOGO_IMG_SIZE) {
              setImageError("");
              readProductImage(file);
              formik.setFieldValue("imageFile", file);
            } else {
              setImageError("File must be JPG/JPEG or PNG format and size must not exceed 10 MB.");
            }
          }
        }}
      />
    </div>
    <div className="ml-3">
      <p className="mb-0" style={{ fontFamily: "Arial", fontSize: "14px", fontWeight: "700", color: "#4B4B55", lineHeight: 1.2 }}>
        Note :
      </p>
      <ul className="ml-4" style={{ fontFamily: "Arial", fontSize: "14px", fontWeight: "400", color: "#000", lineHeight: 1.2 }}>
        <li>Only JPG/JPEG or PNG files are allowed.</li>
        <li>Max file size allowed is 10MB.</li>
        <li>Thumbnail Dimensions: 350W x 200H</li>
      </ul>
    </div>
  </div>
  <div className="d-flex align-items-center justify-content-start text-danger" style={{ display: imageError ? "block" : "none" }} role="alert">
    <small>{imageError}</small>
  </div>
</div>


        <div className="text-right border-top p-3">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleReset();
              history.push(backPath);
            }}
            className="btn btn-outline-dark mr-3 company-button"
          >
            Cancel
          </button>
          {accessPermissionAdd && (
            <button type="button" className="btn btn-primary company-button" onClick={handleSubmitDocument} disabled={false}>
              {editData ? "Update" : "Save"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageModal;

ImageModal.propTypes = {
  accessRights: PropTypes.object,
  location: PropTypes.object,
  computedMatch: PropTypes.object,
  editData: PropTypes.bool,
};
